import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import Link from "next/link";
import { useInfiniteQuery } from "@tanstack/react-query";
import styled from "styled-components";

import {
  AASpinner,
  BackLink,
  Fields,
  FieldType,
  FilterBar,
  FixedToolbarItem,
  FlexToolbarItem,
  Page,
  Toolbar,
} from "@bagel-web/components";
import EventStreamHeaderDetail from "./EventStreamHeaderDetail";
import { CommonAPI } from "../types";
import MomentNavigator from "./MomentNavigator";
import useFilters from "../filter/useFilters";
import MessageTypeSelect from "./MessageTypeSelect";
import useShowMessageTypes from "./useShowMessageTypes";
import { decodeFilterOptions } from "./filterOptions";
import { eventStreamQueries } from "../queries";
import DisplayMoment from "./DisplayMoment";
import {
  getMessageTableColumnVisibility,
  MessageTableHeader,
} from "./message/Message";
import { APIError } from "../util/api";
import FunctionResultSelect from "./FunctionResultSelect";
import { getAgencyTagFilter } from "../tag/AgencyTagFilter";
import {
  AgentViewPreferencesProvider,
  useAgentViewPreferences,
} from "../agent/viewPreferences/agentViewPreferences";
import MessageOrderingToggle from "../agent/viewPreferences/MessageOrderingToggle";
import AgentViewPreferencesButton from "../agent/viewPreferences/AgentViewPreferencesButton";
import { LocalStorageKeys } from "../util/localStorage";

const ScrollSpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 10px;
`;

interface EventStreamProps {
  sessionId: string;
  agentId: string;
  showChalkLogLinks?: boolean;
  commonAPI: CommonAPI;
}

const EventStream = ({
  sessionId,
  agentId,
  showChalkLogLinks = false,
  commonAPI,
}: EventStreamProps) => {
  const startOfStream = useRef<null | HTMLDivElement>(null);
  const endOfStream = useRef<null | HTMLDivElement>(null);
  const scrollContainer = useRef<null | HTMLDivElement>(null);

  const { showNewestMessagesFirst } = useAgentViewPreferences();
  const [highlightedFnCallId, setHighlightedFnCallId] = useState<string | null>(
    null
  );

  const filterFields = useMemo<Fields>(
    () => [
      {
        name: "messageTypes",
        label: "Message Types",
        type: FieldType.MessageType,
        Component: (props) => (
          <MessageTypeSelect
            {...props}
            fetchMessageTypes={commonAPI.fetchMessageTypes}
          />
        ),
        colSpan: 2,
      },
      {
        name: "resultTypes",
        label: "Function Result",
        type: FieldType.FunctionResult,
        Component: FunctionResultSelect,
        colSpan: 2,
      },
    ],
    [commonAPI.fetchMessageTypes]
  );

  const { filterCriteria, handleSetFilters, handleClearFilter } = useFilters(
    filterFields,
    LocalStorageKeys.Filters.EVENT_STREAM
  );

  const showMessageTypes = useShowMessageTypes(
    commonAPI.fetchMessageTypes,
    filterCriteria["messageTypes"]?.value
  );

  const showFunctionResults = decodeFilterOptions(
    filterCriteria["resultTypes"]?.value
  ).map((option) => option.value);

  const {
    data: eventStream,
    refetch,
    error,
    isError,
    isRefetching,
    isPending,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: eventStreamQueries.get(
      sessionId,
      agentId,
      showNewestMessagesFirst
    ),
    queryFn: ({ pageParam }: { pageParam: string | null }) =>
      commonAPI.fetchEventStream({
        sessionId,
        agentId,
        reverse: showNewestMessagesFirst,
        startsAfter: pageParam,
      }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_page_starting_after,
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (intersectionEntries: Array<IntersectionObserverEntry>) => {
        intersectionEntries?.forEach((entry) => {
          if (eventStream && entry.isIntersecting) {
            fetchNextPage();
          }
        });
      },
      { root: scrollContainer.current, rootMargin: "200px" }
    );

    if (endOfStream.current) {
      observer.observe(endOfStream.current);
    }

    return () => {
      if (endOfStream.current) {
        observer.unobserve(endOfStream.current);
      }
    };
  }, [eventStream]);

  const moments = eventStream?.pages.flatMap((page) => page.items);

  const columnVisibility = useMemo(
    () => getMessageTableColumnVisibility(moments ?? []),
    [moments]
  );

  let body: React.ReactNode;
  if (isError) {
    if (error instanceof APIError && error.error.type === "not_ready") {
      body = (
        <Alert variant="info">No messages exist in this stream yet.</Alert>
      );
    } else {
      body = (
        <Alert variant="warning">
          Failed to load message stream: {error.message}
        </Alert>
      );
    }
  } else {
    body = (
      <>
        <div ref={startOfStream} />

        {isPending && <AASpinner />}
        {eventStream && (!moments || moments.length === 0) && (
          <div>No moments found for session</div>
        )}
        {moments && moments.length > 0 && (
          <table style={{ width: "100%" }}>
            <MessageTableHeader columnVisibility={columnVisibility} />
            <tbody>
              {moments.map((moment) => {
                return (
                  <DisplayMoment
                    key={moment.id}
                    agentId={agentId}
                    sessionId={sessionId}
                    moment={moment}
                    commonAPI={commonAPI}
                    highlightedFnCallId={highlightedFnCallId}
                    onHighlightFnCallId={setHighlightedFnCallId}
                    showMessageTypes={showMessageTypes}
                    showFunctionResults={showFunctionResults}
                    showChalkLogLinks={showChalkLogLinks}
                    columnVisibility={columnVisibility}
                    agencyTagFilter={
                      getAgencyTagFilter(
                        []
                      ) /* TODO: Support AgencyTag filter in Message Stream */
                    }
                  />
                );
              })}
            </tbody>
          </table>
        )}
        <div ref={endOfStream} />
        {hasNextPage && (
          <ScrollSpinnerContainer>
            <Spinner />
          </ScrollSpinnerContainer>
        )}
      </>
    );
  }
  return (
    <Page title="Message Stream">
      <Page.Header className="shadow">
        <BackLink href={`/sessions/${sessionId}`} LinkComponent={Link}>
          Session Details
        </BackLink>
        <Toolbar>
          <h2>Message Stream</h2>
          <FlexToolbarItem>
            <EventStreamHeaderDetail
              sessionId={sessionId}
              agentId={agentId}
              commonAPI={commonAPI}
            />
          </FlexToolbarItem>
          <FixedToolbarItem>
            <MessageOrderingToggle />
          </FixedToolbarItem>
          <FixedToolbarItem>
            <AgentViewPreferencesButton />
          </FixedToolbarItem>
          <FixedToolbarItem>
            <FilterBar
              fields={filterFields}
              criteria={filterCriteria}
              onSetFilters={handleSetFilters}
              onClear={handleClearFilter}
              showChips={false}
            />
          </FixedToolbarItem>
          <FixedToolbarItem>
            <MomentNavigator
              startOfStream={startOfStream.current}
              endOfStream={endOfStream.current}
              moments={moments || []}
              refetch={refetch}
              isRefetching={isRefetching}
              reverse={showNewestMessagesFirst}
            />
          </FixedToolbarItem>
        </Toolbar>
      </Page.Header>
      <Page.Body ref={scrollContainer}>{body}</Page.Body>
    </Page>
  );
};

const EventStreamWithPreferences = ({
  sessionId,
  agentId,
  showChalkLogLinks = false,
  commonAPI,
}: EventStreamProps) => {
  return (
    <AgentViewPreferencesProvider>
      <EventStream
        sessionId={sessionId}
        agentId={agentId}
        showChalkLogLinks={showChalkLogLinks}
        commonAPI={commonAPI}
      />
    </AgentViewPreferencesProvider>
  );
};

export default EventStreamWithPreferences;
